import { isMobile, isServerRendering } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import * as React from 'react';
import ReactSlick, { Settings } from 'react-slick';

import { PictureProfile } from '../../../ResizedImage/PictureProfile.csharp';
import { ResponsivePicture } from '../../ReactComponents/ResponsivePicture/ResponsivePicture';
import { ImageGalleryItemViewModel } from './ImageGalleryItemViewModel.csharp';

interface ImageGalleryThumbnailsProps {
    images: ImageGalleryItemViewModel[];
    pictureProfile: PictureProfile;
    selectedImage: number;
    selectImage: (index: number) => void;
    onlyHorizontal?: boolean;
}

export const ImageGalleryThumbnails = ({ images, pictureProfile, selectedImage, selectImage, onlyHorizontal }: ImageGalleryThumbnailsProps) => {
    const thumbnailsContainer = React.createRef<HTMLDivElement>();

    const [slidesToShow, setSlideToShow] = React.useState(1);
    const [screenWidth, setScreenWidth] = React.useState(0);
    const [maxImagesWithoutSliderAmount, setMaxImagesWithoutSliderAmount] = React.useState(1);

    React.useEffect(() => {
        window?.addEventListener('resize', resizeEventHandler);

        return () => {
            window?.removeEventListener('resize', resizeEventHandler);
        };
    }, []);

    React.useEffect(() => {
        calculateSlidesToShow();
    }, [thumbnailsContainer, screenWidth]);

    const calculateSlidesToShow = () => {
        const defaultMobileSize = 48;
        const defaultDesktopSize = 65;
        const thumbnailSize = isMobile() ? defaultMobileSize : defaultDesktopSize;

        if (thumbnailsContainer.current) {
            const thumbnailsContainerDimension = getThumbnailsContainerDimension();
            const maxSlides = Math.floor(thumbnailsContainerDimension / thumbnailSize);
            setMaxImagesWithoutSliderAmount(maxSlides);
            setSlideToShow(maxSlides <= images.length ? maxSlides : images.length);
        }
    };

    const getThumbnailsContainerDimension = (): number => {
        if (!thumbnailsContainer.current) return 0;
        const styles = getComputedStyle(thumbnailsContainer.current);

        const { offsetHeight, offsetWidth } = thumbnailsContainer.current;

        const calculatedWidth = offsetWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);

        if (onlyHorizontal) return calculatedWidth;
        return isMobile() ? calculatedWidth : offsetHeight;
    };

    const resizeEventHandler = () => {
        !isServerRendering() && setScreenWidth(window.innerWidth);
    };

    const settings = {
        dots: false,
        infinite: false,
        slidesToShow: slidesToShow,
        slidesToScroll: 1,
        vertical: !isMobile() && !onlyHorizontal,
        swipe: false,
        useTransform: false
    } as Settings;

    const getItems = () => {
        return images.map((image, index) => {
            return (
                <button
                    onClick={() => selectImage(index)}
                    key={index}
                    className={classNames(
                        'ImageGallery__Thumbnail',
                        index === selectedImage && 'ImageGallery__Thumbnail--Active',
                        image.picture.responsiveData.shouldUseFocusArea && 'ImageGallery__Thumbnail--Crop'
                    )}
                >
                    <ResponsivePicture model={image.picture} profile={pictureProfile} isLazyLoaded />
                </button>
            );
        });
    };

    const shouldRenderSlick = maxImagesWithoutSliderAmount !== 0 && images.length > maxImagesWithoutSliderAmount;

    return (
        <div
            className={classNames(
                'ImageGallery__Thumbnails',
                shouldRenderSlick && 'ImageGallery__Thumbnails--WithSlick',
                images.length <= 1 && 'ImageGallery__Thumbnails--SingleImage'
            )}
            ref={thumbnailsContainer}
        >
            {shouldRenderSlick ? <ReactSlick {...settings}>{getItems()}</ReactSlick> : getItems()}
        </div>
    );
};
